import { useState } from 'react';
import clsx from 'clsx';
import { Mail, Plus } from 'lucide-react';
import { Button } from '@components/common/Button';
import { Card, CardTitle } from '@components/common/Card';
import PlanGate from '@components/common/PlanGate';
import UpgradeBanner from '@components/common/UpgradeBanner';
import { MailboxConnectionCard } from './MailboxConnectionCard';
import {
  useMailboxConnections,
  useDeleteMailboxConnection,
  usePauseMailboxConnection,
  useResumeMailboxConnection,
  usePollMailboxNow,
} from '@api/hooks/useMailbox';
import { apiClient } from '@api/client';
import type { MailboxOAuthUrlResponse } from '@schemas/api';

interface MailboxSettingsProps {
  mode?: 'full' | 'onboarding';
  onBeforeOAuthRedirect?: () => void;
}

const PROVIDERS = [
  { key: 'microsoft', label: 'Microsoft 365 / Outlook', available: true },
  { key: 'gmail', label: 'Gmail', available: true },
] as const;

export const MailboxSettings = ({ mode = 'full', onBeforeOAuthRedirect }: MailboxSettingsProps) => {
  const { data: connectionsData, isLoading } = useMailboxConnections();
  const deleteMutation = useDeleteMailboxConnection();
  const pauseMutation = usePauseMailboxConnection();
  const resumeMutation = useResumeMailboxConnection();
  const pollMutation = usePollMailboxNow();

  const [showAddFlow, setShowAddFlow] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const connections = connectionsData?.connections ?? [];
  const hasConnections = connections.length > 0;

  const handleOAuthConnect = async (provider: string) => {
    setIsRedirecting(true);
    try {
      const { data } = await apiClient.get<MailboxOAuthUrlResponse>(
        `/auth/mailbox/${provider}/url`
      );
      onBeforeOAuthRedirect?.();
      window.location.href = data.authorization_url;
    } catch {
      setIsRedirecting(false);
    }
  };

  // Onboarding mode: simplified layout
  if (mode === 'onboarding') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Mailbox Integration
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Automatically pull invoices and documents from your email inbox.
          </p>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : hasConnections ? (
          <div className="space-y-3">
            {connections.map((conn) => (
              <MailboxConnectionCard
                key={conn.id}
                connection={conn}
                onPause={(id) => pauseMutation.mutate(id)}
                onResume={(id) => resumeMutation.mutate(id)}
                onDelete={(id) => deleteMutation.mutate(id)}
                onPollNow={(id) => pollMutation.mutate(id)}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {PROVIDERS.map((provider) => (
              <button
                key={provider.key}
                type="button"
                onClick={() => provider.available && handleOAuthConnect(provider.key)}
                disabled={!provider.available || isRedirecting}
                className={clsx(
                  'relative flex flex-col items-center gap-3 rounded-lg border-2 p-6 transition-colors',
                  provider.available
                    ? 'border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 cursor-pointer'
                    : 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                )}
              >
                <Mail
                  className={clsx(
                    'h-8 w-8',
                    provider.available
                      ? 'text-gray-500 dark:text-gray-400'
                      : 'text-gray-400 dark:text-gray-500'
                  )}
                />
                <span
                  className={clsx(
                    'text-sm font-medium',
                    provider.available
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-gray-400 dark:text-gray-500'
                  )}
                >
                  {provider.label}
                </span>
                {!provider.available && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Coming Soon
                  </span>
                )}
                {provider.available && isRedirecting && (
                  <span className="text-xs text-primary-600 dark:text-primary-400">
                    Redirecting...
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full mode: always-expanded card for profile page
  return (
    <PlanGate feature="email_ingestion" fallback={<UpgradeBanner feature="email_ingestion" />}>
    <Card padding={false}>
      <div className="p-6 space-y-4">
        <CardTitle>Mailbox Connections</CardTitle>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading connections...</p>
        ) : hasConnections ? (
          <div className="space-y-3">
            {connections.map((conn) => (
              <MailboxConnectionCard
                key={conn.id}
                connection={conn}
                onPause={(id) => pauseMutation.mutate(id)}
                onResume={(id) => resumeMutation.mutate(id)}
                onDelete={(id) => deleteMutation.mutate(id)}
                onPollNow={(id) => pollMutation.mutate(id)}
                isPausing={pauseMutation.isPending}
                isResuming={resumeMutation.isPending}
                isDeleting={deleteMutation.isPending}
                isPolling={pollMutation.isPending}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No mailbox connections configured.
          </p>
        )}

        {!showAddFlow ? (
          <Button
            variant="secondary"
            onClick={() => setShowAddFlow(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Mailbox Connection
          </Button>
        ) : (
          <div className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Select a provider:
            </p>
            <div className="flex gap-3">
              {PROVIDERS.map((provider) => (
                <Button
                  key={provider.key}
                  variant="primary"
                  onClick={() => handleOAuthConnect(provider.key)}
                  disabled={!provider.available || isRedirecting}
                  loading={isRedirecting}
                >
                  <Mail className="h-4 w-4 mr-1.5" />
                  {provider.label}
                  {!provider.available && ' (Coming Soon)'}
                </Button>
              ))}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowAddFlow(false)}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </Card>
    </PlanGate>
  );
};
